import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const FeatureContext = createContext(null);

const API_BASE = '/api/auth';

// Tier order for comparison
const TIER_ORDER = ['basic', 'paid', 'plus'];

// Default features for basic tier (used as fallback)
const DEFAULT_FEATURES = {
  tier: 'basic',
  tierInfo: {
    name: 'Basic',
    price: 'Free',
    priceMonthly: 0,
  },
  features: {
    tradeReasoning: true,
    performanceMetrics: true,
    weeklyRecap: true,
    telegramNotifications: false,
    decisionLog: false,
    apiAccess: false,
    customStrategies: false,
  },
  canUpgrade: true,
  nextTier: {
    tier: 'paid',
    name: 'Paid',
    price: '$9.90/month',
    priceMonthly: 9.90,
  },
};

export function FeatureProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [featureInfo, setFeatureInfo] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);

  // Fetch features when auth state changes
  useEffect(() => {
    fetchFeatures();
  }, [isAuthenticated, user?.subscriptionTier]);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${API_BASE}/features`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFeatureInfo(data);
      } else {
        // Use default features if fetch fails
        setFeatureInfo(DEFAULT_FEATURES);
      }
    } catch (err) {
      console.error('Features fetch failed:', err);
      setFeatureInfo(DEFAULT_FEATURES);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has access to a tier level
   * @param {string} requiredTier - The tier level required
   * @returns {boolean}
   */
  const hasAccess = (requiredTier) => {
    const userTierIndex = TIER_ORDER.indexOf(featureInfo.tier || 'basic');
    const requiredTierIndex = TIER_ORDER.indexOf(requiredTier || 'basic');

    if (userTierIndex === -1 || requiredTierIndex === -1) {
      return false;
    }

    return userTierIndex >= requiredTierIndex;
  };

  /**
   * Check if a specific feature is enabled
   * @param {string} featureName - The feature to check
   * @returns {boolean}
   */
  const hasFeature = (featureName) => {
    return featureInfo.features[featureName] || false;
  };

  /**
   * Get the minimum tier required for a feature
   * @param {string} featureName - The feature to check
   * @returns {string} Tier name
   */
  const getMinimumTierForFeature = (featureName) => {
    // Feature -> minimum tier mapping
    const featureTierMap = {
      tradeReasoning: 'basic',
      performanceMetrics: 'basic',
      weeklyRecap: 'basic',
      telegramNotifications: 'paid',
      decisionLog: 'paid',
      apiAccess: 'plus',
      customStrategies: 'plus',
    };

    return featureTierMap[featureName] || 'basic';
  };

  /**
   * Get info about a tier
   * @param {string} tier - Tier name
   * @returns {object} Tier info
   */
  const getTierInfo = (tier) => {
    const tierInfoMap = {
      basic: { name: 'Basic', price: 'Free', priceMonthly: 0 },
      paid: { name: 'Paid', price: '$9.90/month', priceMonthly: 9.90 },
      plus: { name: 'Plus', price: '$29.99/month', priceMonthly: 29.99 },
    };

    return tierInfoMap[tier] || tierInfoMap.basic;
  };

  const value = {
    tier: featureInfo.tier,
    tierInfo: featureInfo.tierInfo,
    features: featureInfo.features,
    canUpgrade: featureInfo.canUpgrade,
    nextTier: featureInfo.nextTier,
    loading,
    hasAccess,
    hasFeature,
    getMinimumTierForFeature,
    getTierInfo,
    refreshFeatures: fetchFeatures,
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
}

export default FeatureContext;
